import { compose, withProps } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';
import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import Naptime from 'bundles/naptimejs';
import PromotionEligbility from 'bundles/naptimejs/resources/promotionEligibilities.v1';
import type {
  FindByEnterprisePromotionIdQuery as FindByEnterprisePromotionIdQueryType,
  FindByEnterprisePromotionIdQueryVariables,
  FindByEnterprisePromotionIdQuery_PromotionsV3Resource_byEnterprisePromotionId_elements as UnderlyingPromo,
} from 'bundles/teams-v2/queries/__generated__/FindByEnterprisePromotionIdQuery';
import type {
  FindByPromoCodeQuery_EnterprisePromotionsV1Resource_findByPromoCode_elements as FindByPromo,
  FindByPromoCodeQuery as FindByPromoCodeQueryType,
  FindByPromoCodeQueryVariables,
} from 'bundles/teams-v2/queries/__generated__/FindByPromoCodeQuery';
import { findByEnterprisePromotionIdQuery, findByPromoCodeQuery } from 'bundles/teams-v2/queries/assemblerQueries';
import type { WithRouterFreeTrialProps } from 'bundles/teams-v2/utils/withFreeTrial';

export type PropsFromGraphQlEnterprisePromotionMap = {
  enterprisePromotion?: Pick<FindByPromo, 'id' | 'promotionId'>;
};

export type PropsFromGraphQlUnderlyingPromotionMap = {
  promotion?: Pick<UnderlyingPromo, 'id' | 'promotionName' | 'endsAt' | 'startsAt' | 'discountPercent'>;
};

export type PropsFromPromotionEligibility = {
  isPromotionEligible?: boolean;
};

export type WithRouterPromoCodeProps = {
  promoCode?: string;
};

export type WithPromoCodeToPromotionProps = WithRouterPromoCodeProps &
  PropsFromGraphQlUnderlyingPromotionMap &
  PropsFromGraphQlEnterprisePromotionMap;

/** @deprecated Use useRouterPromoCode instead */
export const withRouterPromoCode = connectToRouter<WithRouterPromoCodeProps, {}>((router) => {
  return { promoCode: router.location.query?.promoCode };
});

export const useRouterPromoCode = (): WithRouterPromoCodeProps => {
  const location = useLocation();
  return { promoCode: simplifyQueryValue(location.query?.promoCode) };
};

export const findByPromoCodeQueryData = waitForGraphQL<
  WithRouterPromoCodeProps & WithRouterFreeTrialProps & PropsFromGraphQlEnterprisePromotionMap,
  FindByPromoCodeQueryType,
  FindByPromoCodeQueryVariables,
  WithPromoCodeToPromotionProps
>(findByPromoCodeQuery, {
  skip: ({ promoCode, freeTrial }: WithRouterPromoCodeProps & WithRouterFreeTrialProps) => freeTrial || !promoCode,
  options: ({ promoCode }: WithRouterPromoCodeProps) => ({
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      promoCodeId: promoCode!,
    },
    errorPolicy: 'all',
  }),
  props: ({ data, ownProps }) => {
    const enterprisePromotionV1 = data?.EnterprisePromotionsV1Resource?.findByPromoCode?.elements[0];
    if (!enterprisePromotionV1) {
      return {};
    }
    return {
      promoCode: ownProps.promoCode,
      enterprisePromotion: {
        id: enterprisePromotionV1.id,
        promotionId: enterprisePromotionV1.promotionId,
      },
    };
  },
});

export const findByEnterprisePromotionIdData = waitForGraphQL<
  WithRouterPromoCodeProps & PropsFromGraphQlEnterprisePromotionMap & PropsFromGraphQlUnderlyingPromotionMap,
  FindByEnterprisePromotionIdQueryType,
  FindByEnterprisePromotionIdQueryVariables,
  WithPromoCodeToPromotionProps
>(findByEnterprisePromotionIdQuery, {
  skip: ({ enterprisePromotion }: PropsFromGraphQlEnterprisePromotionMap) => !enterprisePromotion,
  options: ({ enterprisePromotion }) => ({
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: enterprisePromotion!.id,
    },
    errorPolicy: 'all',
  }),
  props: ({ data, ownProps }) => {
    const promotionsV3 = data?.PromotionsV3Resource?.byEnterprisePromotionId?.elements?.[0];
    if (!promotionsV3) {
      return {
        promoCode: ownProps.promoCode,
        enterprisePromotion: ownProps.enterprisePromotion,
      };
    }
    return {
      promoCode: ownProps.promoCode,
      enterprisePromotion: ownProps.enterprisePromotion,
      promotion: promotionsV3,
    };
  },
});

export const withPromoCodeToPromotion = compose<
  WithPromoCodeToPromotionProps,
  WithRouterPromoCodeProps & WithRouterFreeTrialProps
>(findByPromoCodeQueryData, findByEnterprisePromotionIdData);

type PromoEligibilitySlice = {
  id: string;
  eligibilityCode?: string;
};

type WithPromotionEligibilityNaptimeOuter = {
  promoCode?: string;
  promotion?: UnderlyingPromo;
};

type WithPromotionEligibilityNaptimeInner = {
  promoEligibility?: PromoEligibilitySlice;
};

type WithPromotionEligibilityWithInner = {
  isPromotionEligible: boolean;
};

type WithPromotionEligibilityWithOuter = {
  promoEligibility?: PromoEligibilitySlice;
};

export const withPromotionEligibility = compose<PropsFromPromotionEligibility, WithPromoCodeToPromotionProps>(
  Naptime.createContainer<WithPromotionEligibilityNaptimeInner, WithPromotionEligibilityNaptimeOuter>(
    ({ promoCode, promotion }) => {
      if (!promoCode || !promotion) {
        return {};
      }

      return {
        promoEligibility: PromotionEligbility.get(`${promoCode}~EnterpriseContract!~0`, {
          fields: ['id', 'eligibilityCode'],
        }),
      };
    }
  ),
  withProps<WithPromotionEligibilityWithInner, WithPromotionEligibilityWithOuter>(({ promoEligibility }) => {
    return {
      isPromotionEligible: promoEligibility?.eligibilityCode === 'ELIGIBLE',
    };
  })
);
